import {
  Box, Divider, FormControl, FormLabel, Input, Stack, Text,
} from "@chakra-ui/react"
import React from "react"
import RadioButtons, { RadioOptionLabel } from "components/ReactHookForm/RadioButtons"
import { Boolean, DEFAULT_SENDER_EMAIL } from "utilities/enums"
import ContainedForm from "components/ReactHookForm/ContainedForm"
import TextField from "components/ReactHookForm/TextField"
import TextArea from "components/ReactHookForm/TextArea"
import Select from "components/ReactHookForm/Select"
import { useCurrentEvent } from "queries/events"
import * as Yup from "yup"
import { hasValidCustomDomain } from "utilities/strings"
import { EMAIL_USER_VALIDATION_REGEX } from "utilities/constants"
import { notificationsReplyToEmailOptions } from "utilities/objects"
import { AccountUser } from "sharedTypes"
import PageContent from "components/layout/PageContent"
import Checkbox from "components/ReactHookForm/Checkbox"
import TitleWithSubtitle from "../TitleWithSubtitle"

type Props = {
  updateEvent: (params: {}) => Promise<void>
  accountUsers: AccountUser[]
}

const EmailResponders = ({ updateEvent, accountUsers }: Props) => {
  const { data: currentEvent, data: { sendConfirmationEmails, users } } = useCurrentEvent()

  const validCustomDomain = hasValidCustomDomain(currentEvent)

  const validationSchema = Yup.object({
    emailNotificationsSenderMailbox: validCustomDomain
      ? Yup.string()
        .matches(
          EMAIL_USER_VALIDATION_REGEX,
          "Invalid email",
        )
      : Yup.string().nullable(),
  })

  const replyToOptions = notificationsReplyToEmailOptions(
    users, accountUsers, currentEvent.emailNotificationsReplyTo,
  )

  const onSubmit = (values) => updateEvent(values)

  return (
    <PageContent noPadding>
      <Box>
        <TitleWithSubtitle
          title="Automated Emails for Guests"
          subtitle="Diobox can automatically email guests when they register, RSVP, buy a ticket, join a waitlist, or complete a survey."
        />
        <ContainedForm
          values={{ sendConfirmationEmails: sendConfirmationEmails ? Boolean.True : Boolean.False }}
          onSubmit={({ sendConfirmationEmails: enabled }) => onSubmit({
            sendConfirmationEmails: enabled === Boolean.True,
          })}
          name="Automated Emails for Guests"
        >
          {({ control }) => (
            <Box pt={4}>
              <RadioButtons
                name="sendConfirmationEmails"
                control={control}
                spacing={6}
                options={[
                  {
                    label: (
                      <RadioOptionLabel
                        title="Send confirmation emails (Recommended)"
                        description={(
                          <Text>Guests get a confirmation email each time they submit a form.</Text>
                        )}
                      />
                    ),
                    value: Boolean.True,
                  },
                  {
                    label: (
                      <RadioOptionLabel
                        title="Don't send confirmation emails"
                        description={(
                          <Text>Guests won&apos;t receive automated confirmation emails.</Text>
                        )}
                      />
                    ),
                    value: Boolean.False,
                  },
                ]}
              />
            </Box>
          )}
        </ContainedForm>
      </Box>
      <Box mt={16}>
        <TitleWithSubtitle
          title="Sender & Message Details"
          subtitle="These details are used in all automated emails from this event, for guests and team members."
        />
        <ContainedForm
          values={currentEvent}
          onSubmit={onSubmit}
          name="Sender & Message Details"
          validationSchema={validationSchema}
        >
          {({ control }) => (
            <Stack spacing={6}>
              <Box>
                <TextField
                  name="emailNotificationsSenderName"
                  control={control}
                  label="Sender Name"
                  variant="outline"
                />
                {validCustomDomain ? (
                  <TextField
                    name="emailNotificationsSenderMailbox"
                    control={control}
                    label="Sender Email"
                    variant="outline"
                    rightElement={`@${currentEvent.customDomain?.domain}`}
                  />
                ) : (
                  <>
                    <FormControl display="flex" alignItems="flex-start" mb={4}>
                      <FormLabel w={1 / 4} mt={2}>Sender Email</FormLabel>
                      <Input
                        value={DEFAULT_SENDER_EMAIL}
                        variant="outline"
                        isReadOnly
                        bg="gray.50"
                        _readOnly={{ color: "gray.500" }}
                      />
                    </FormControl>
                    <Box sx={{ "& label": { mb: 0 } }}>
                      <Select
                        name="emailNotificationsReplyTo"
                        control={control}
                        label="Reply-To Email"
                        options={replyToOptions}
                        fullWidth
                      />
                    </Box>
                  </>
                )}
              </Box>
              <Divider mx={-6} w="auto" />
              <Checkbox
                name="showDateInConfirmationEmail"
                control={control}
                label="Show event date below the event title"
                leftLabel="Event Date"
                mb={0}
              />
              <Divider mx={-6} w="auto" />
              <Box>
                <TextArea
                  name="emailNotificationsCustomMessage"
                  control={control}
                  label="Custom Message"
                  helperText="Appears at the bottom of the email."
                  h={48}
                />
              </Box>
            </Stack>
          )}
        </ContainedForm>
      </Box>
    </PageContent>
  )
}

export default EmailResponders
